import type { ReactNode } from "react"
import { createElement } from "react"

export const formatDurationHours = (hours: number): string => {
	if (!Number.isFinite(hours) || hours < 0) return "—"
	if (hours >= 24) {
		const days = Math.floor(hours / 24)
		const remainingHours = Math.round(hours - days * 24)
		return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
	}
	const totalMinutes = Math.round(hours * 60)
	if (totalMinutes < 60) return `${totalMinutes}min`
	const h = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	return minutes > 0 ? `${h}:${minutes.toString().padStart(2, "0")}h` : `${h}h`
}

export const formatDurationHoursNodes = (hours: number): ReactNode => {
	const unitClass = "text-[1.3rem] font-medium text-secondary mr-1 ml-0.5"
	if (!Number.isFinite(hours) || hours < 0) return "—"
	if (hours >= 24) {
		const days = Math.floor(hours / 24)
		const remainingHours = Math.round(hours - days * 24)
		return remainingHours > 0
			? createElement(
					"span",
					null,
					days,
					createElement("span", { className: unitClass }, "d "),
					remainingHours,
					createElement("span", { className: unitClass }, "h"),
				)
			: createElement(
					"span",
					null,
					days,
					createElement("span", { className: unitClass }, "d"),
				)
	}
	const totalMinutes = Math.round(hours * 60)
	if (totalMinutes < 60) {
		return createElement(
			"span",
			null,
			totalMinutes,
			createElement("span", { className: unitClass }, "min"),
		)
	}
	const h = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	return minutes > 0
		? createElement(
				"span",
				null,
				`${h}:${minutes.toString().padStart(2, "0")}`,
				createElement("span", { className: unitClass }, "h"),
			)
		: createElement(
				"span",
				null,
				h,
				createElement("span", { className: unitClass }, "h"),
			)
}

export const median = (values: number[]): number | null => {
	if (values.length === 0) return null
	const sorted = [...values].sort((a, b) => a - b)
	const mid = Math.floor(sorted.length / 2)
	const low = sorted[mid - 1]
	const high = sorted[mid]
	if (sorted.length % 2 === 0 && low !== undefined && high !== undefined) {
		return (low + high) / 2
	}
	return high ?? low ?? null
}

export const percentile = (values: number[], p: number): number | null => {
	if (values.length === 0) return null
	const sorted = [...values].sort((a, b) => a - b)
	const index = Math.min(
		sorted.length - 1,
		Math.max(0, Math.ceil((p / 100) * sorted.length) - 1),
	)
	return sorted[index] ?? null
}
